import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { apiUrl } from "../config";
import { translateText } from "../utils/translateText";

export default function DashboardTips() {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const [profile, setProfile] = useState(null);
  const [advice, setAdvice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const bmi = useMemo(() => Number(profile?.bmi) || 0, [profile?.bmi]);

  useEffect(() => {
    const stored = localStorage.getItem("profileData");

    if (stored) {
      setProfile(JSON.parse(stored));
    } else {
      const height = localStorage.getItem("userHeight");
      const weight = localStorage.getItem("userWeight");
      const bmi = localStorage.getItem("lastBMI");
      const age = localStorage.getItem("userAge");
      const gender = localStorage.getItem("userGender");

      if (height && weight && bmi) {
        setProfile({ height, weight, bmi, age, gender });
      } else {
        setErr(t("personalized_tips_error"));
      }
    }
  }, [t]);

  useEffect(() => {
    if (!bmi) return;

    const ctrl = new AbortController();
    (async () => {
      try {
        setLoading(true);
        const response = await fetch(apiUrl("/api/bmi-advice"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            bmi,
            age: profile?.age,
            gender: profile?.gender,
            heightCm: profile?.height,
            weightKg: profile?.weight,
          }),
          signal: ctrl.signal,
        });

        const data = await response.json();
        if (typeof data?.advice === "string") {
          const translated = ["es", "vi","ta"].includes(language)
            ? await translateText(data.advice, language)
            : data.advice;
          setAdvice({ summary: translated });
        } else if (data?.advice?.summary) {
          const { summary, dietTips = [], activityTips = [], cautions = [] } = data.advice;

          const translatedSummary = ["es", "vi","ta"].includes(language)
            ? await translateText(summary, language)
            : summary;

          const translateList = async (arr) => {
            return ["es", "vi","ta"].includes(language)
              ? Promise.all(arr.map((tip) => translateText(tip, language)))
              : arr;
          };

          const translatedDiet = await translateList(dietTips);
          const translatedActivity = await translateList(activityTips);
          const translatedCautions = await translateList(cautions);

          setAdvice({
            summary: translatedSummary,
            dietTips: translatedDiet,
            activityTips: translatedActivity,
            cautions: translatedCautions,
          });
        } else {
          setErr(t("personalized_tips_error"));
        }
      } catch (e) {
        if (e.name !== "AbortError") {
          console.error("Home tips fetch failed", e);
          setErr(t("personalized_tips_error"));
        }
      } finally {
        setLoading(false);
      }
    })();

    return () => ctrl.abort();
  }, [bmi, profile, t, language]);

  const combinedTips = useMemo(() => {
    if (!advice) return [];
    return [
      ...(advice.dietTips || []),
      ...(advice.activityTips || []),
      ...(advice.cautions || []),
    ].slice(0, 3); // show only 3 tips
  }, [advice]);

  return (
    <div
      className="home-card"
      style={{
        background: "#141414",
        border: "1px solid #0a3d4f",
        borderRadius: 12,
        padding: "14px 16px",
        minHeight: 180,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
        }}
      >
        <h4 style={{ margin: 0, color: "#00ffcc" }}>{t("personalized_tips")}</h4>
        <Link
  to="/result"
  state={{
    ...profile,
    bmi: profile?.bmi,
  }}
  style={{ color: "#79e5ff", textDecoration: "none" }}
>
  {t("more")} →
</Link>
      </div>

      {loading ? (
        <div style={{ opacity: 0.7, marginTop: 8 }}>{t("generating_tips")}</div>
      ) : err ? (
        <div style={{ marginTop: 10, color: "tomato" }}>{err}</div>
      ) : advice ? (
        <div style={{ marginTop: 10 }}>
          {combinedTips.length > 0 ? (
            <ul style={{ listStyle: "disc", paddingLeft: 16, marginTop: 8 }}>
              {combinedTips.map((tip, i) => (
                <li
                  key={i}
                  style={{
                    marginBottom: 8,
                    borderBottom: "1px dashed #1f2a30",
                    paddingBottom: 6,
                    lineHeight: 1.5,
                  }}
                >
                  {tip}
                </li>
              ))}
            </ul>
          ) : (
            <div
              style={{
                fontWeight: 500,
                lineHeight: "1.7",
                whiteSpace: "pre-line",
                textAlign: "justify",
              }}
            >
              {advice.summary}
            </div>
          )}
          <small style={{ display: "block", color: "gray", marginTop: 12 }}>
            {t("medical_disclaimer")}
          </small>
        </div>
      ) : (
        <div style={{ opacity: 0.7, marginTop: 8 }}>{t("personalized_tips_error")}</div>
      )}
    </div>
  );
}